// @owners { team: patients-team }
import { COLORS, SPACING, type TEXT_COLORS_VALUES } from '@alto/design-library-tokens';
import {
  Card,
  Column,
  ContentLoader,
  H1,
  LgPadding,
  LgSpacing,
  LoadingEmptyState,
  SecondaryPage,
  Separator,
  SmSpacing,
  XsSpacing,
  XxxlPadding,
} from '@alto/design-system';
import {
  AddOnsContext,
  OrderDetailsCard,
  PaymentSummary,
  SharpsContainerDetailCard,
  SplitPaymentCard,
  useGetDeliveryMethodsByShipmentIDs,
} from '@alto/features';
import { type ShipmentTip } from '@alto/scriptdash/alto/orders/types/v1/shipment_tip';
import React, { useCallback, useMemo } from 'react';
import styledNative from 'styled-components/native';
import { SHIPMENTS_API_VERSION } from '~shared/actions/shipments';
import { API_URL } from '~shared/config';
import { getIsTippableForShipmentID } from '~shared/features/checkout/selectors/getIsTippable';
import { canEditCourierTip } from '~shared/features/courier-tips/helpers';
import { ORIGINS } from '~shared/features/essentials/add_ons_consts';
import { checkIfPaymentMethodFailedBeforeSaveOrderAttemptOnExistingOrder } from '~shared/features/payment-methods/helpers/checkIfPaymentMethodFailed';
import { isShipmentEditable } from '~shared/features/shipments/helpers/isShipmentEditable';
import { useHasCompoundPrescriptionsForShipment } from '~shared/features/shipments/hooks/useHasCompoundPrescriptionsForShipment';
import { getShipmentAddress } from '~shared/features/shipments/selectors/getShipments';
import { type ShipmentWithItems } from '~shared/features/shipments/types';
import { useIsFeatureDisabledForCompany } from '~shared/hooks';
import { useDeliveryDetailsViewed } from '~shared/lib/analytics/src/hooks/useDeliveryDetailsViewed';
import { useOrderPricing } from '~shared/queries';
import { useGetShipmentPaymentMethodsWithDetails } from '~shared/queries/query-keys/shipmentPaymentMethods';
import { useSelectorShared } from '~shared/store';
import DeliveryItems from './DeliveryItems';
import { ShipmentSingleCarousel } from '~web/features/homescreen/components/ShipmentSingleCarousel';

const StyledH1 = styledNative(H1)<{ color: TEXT_COLORS_VALUES }>`
  color: ${({ color }) => color};
`;

const ParentWidthLgPadding = styledNative(LgPadding)`
  width: 100%
`;

type OrderDetailsProps = {
  readonly editShipment: () => void;
  readonly openCancelDeliveryPage: () => void;
  readonly openEditOrAddTipModal: () => void;
  readonly shipment: ShipmentWithItems;
  readonly shipmentTip: ShipmentTip | null | undefined;
};

export const OrderDetails = ({
  editShipment,
  openCancelDeliveryPage,
  openEditOrAddTipModal,
  shipment,
  shipmentTip,
}: OrderDetailsProps) => {
  useDeliveryDetailsViewed();
  const shipmentID = shipment.id;
  const { deliveryMethodsByShipmentID } = useGetDeliveryMethodsByShipmentIDs([shipmentID]);
  const deliveryMethod = deliveryMethodsByShipmentID[shipmentID];
  const isTippable = useSelectorShared((state) => getIsTippableForShipmentID(state, { shipmentID, deliveryMethod }));
  const address = useSelectorShared((state) => getShipmentAddress(state, { shipmentID }));
  shipment.address = address;
  const canEditTip = canEditCourierTip(shipment, shipmentTip);
  const { isOrderPricingLoading } = useOrderPricing({ shipmentID });
  const shipmentPaymentMethods = useGetShipmentPaymentMethodsWithDetails({ shipmentID: shipment.id });
  const tipPaymentMethod = shipmentPaymentMethods.find(
    ({ payment_method_id }) => payment_method_id === shipmentTip?.payment_method_id,
  )?.paymentMethod;
  const anyPaymentMethodFailed = shipmentPaymentMethods?.some((spm) =>
    checkIfPaymentMethodFailedBeforeSaveOrderAttemptOnExistingOrder(spm),
  );
  const hasFailedSplitPayments = shipmentPaymentMethods?.length > 1 && anyPaymentMethodFailed;
  const hasCompound = useHasCompoundPrescriptionsForShipment({ shipmentID });
  const shipmentIsEditable = !!shipment && isShipmentEditable(shipment);
  const isEssentialsAutoRefillOptOutAllowed = shipmentIsEditable && !hasCompound;
  const { isFeatureDisabled: shouldDisableOTCForCompany, isLoading: isFeatureDisabledLoading } =
    useIsFeatureDisabledForCompany('otc');
  const shouldShowEssentialsUpsell = isEssentialsAutoRefillOptOutAllowed && !shouldDisableOTCForCompany;

  const essentialsContext = useMemo(
    () =>
      ({
        shipmentID,
        origin: ORIGINS.EDIT_DELIVERY_DETAILS,
      }) as const,
    [shipmentID],
  );

  const handleDownloadReceipt = useCallback(() => {
    const url = `${API_URL}/${SHIPMENTS_API_VERSION}/shipments/${shipmentID}/receipt`;
    window.open(url, '_blank');
  }, [shipmentID]);

  if (isFeatureDisabledLoading) {
    return <LoadingEmptyState />;
  }

  return (
    <AddOnsContext.Provider value={essentialsContext}>
      <SecondaryPage
        HeaderContent={
          <XxxlPadding
            leftPadding={SPACING.STATIC.NONE}
            rightPadding={SPACING.STATIC.NONE}
          >
            <Column centerHorizontally>
              <StyledH1 color={COLORS.TEXT_COLORS.PRIMARY}>Order details</StyledH1>
            </Column>
          </XxxlPadding>
        }
      >
        <OrderDetailsCard
          handleCancelDelivery={openCancelDeliveryPage}
          handleDownloadReceipt={handleDownloadReceipt}
          handleEditDelivery={editShipment}
          shipment={shipment}
        >
          {shouldShowEssentialsUpsell ? (
            <>
              <Separator />
              <LgSpacing />
              <ParentWidthLgPadding topPadding={SPACING.STATIC.NONE}>
                <ShipmentSingleCarousel />
              </ParentWidthLgPadding>
            </>
          ) : null}
        </OrderDetailsCard>
        <SmSpacing />
        <Card>
          <LgPadding>
            <DeliveryItems
              items={shipment.items}
              shipmentIsEditable={isShipmentEditable(shipment)}
              shipment={shipment}
            />
          </LgPadding>
        </Card>
        {shipment.includes_sharps_container && (
          <>
            <SmSpacing />
            <SharpsContainerDetailCard />
          </>
        )}
        <SmSpacing />
        <Card>
          {!isOrderPricingLoading ? (
            <PaymentSummary
              origin="DeliveryDetails"
              openEditTip={canEditTip ? openEditOrAddTipModal : undefined}
              showZeroTipLineItem={isTippable}
              showEditTipOption={canEditTip}
              shipmentPaymentMethods={shipmentPaymentMethods}
              tipPaymentMethod={tipPaymentMethod}
              shipmentID={shipment.id}
              shipment={shipment}
            />
          ) : (
            <ContentLoader accessibilityLabel="loading pricing details" />
          )}
        </Card>
        <XsSpacing />
        <SplitPaymentCard hasFailedSplitPayments={hasFailedSplitPayments} />
        <XsSpacing />
      </SecondaryPage>
    </AddOnsContext.Provider>
  );
};
